/**
 * 任务调度 —— **这一页现在跑的是真台账**(hub 的 /ledger/*)。
 *
 * 原型在这里存了一份 `createdTasks` 本地状态:点"发布任务"就往列表里插一行,
 * 页面看着任务建成了,而后端什么都没发生。刷新一下就没了 —— 建任务失败和
 * 建任务成功在台面上长得一模一样。现在建任务走 POST /ledger/task,建完重拉。
 */
import { useState } from 'react';
import type { Task } from '@/types/fleet';
import { useTasks } from '@/hooks/useHubData';
import { LoadError } from '@/components/feature/DataSource';
import TaskList from './components/TaskList';
import CreateTaskModal from './components/CreateTaskModal';
import TaskDetailPanel from './components/TaskDetailPanel';

function StatCard({ label, value, color, icon }: { label: string; value: string | number; color: string; icon: string }) {
  return (<div className="bg-background-50 border border-background-200/70 rounded-lg p-4 card-lift"><div className="flex items-center gap-2 mb-2"><span className={`w-7 h-7 flex items-center justify-center rounded-md ${color}`}><i className={`${icon} text-xs`} /></span><span className="text-[11px] text-foreground-500">{label}</span></div><div className="text-xl font-bold text-foreground-900 font-mono">{value}</div></div>);
}

export default function TasksPage() {
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { tasks, taskUnits, loading, error, refetch } = useTasks();

  const activeCount = tasks.filter((t) => t.status === 'in_progress' || t.status === 'pending').length;
  const completedCount = tasks.filter((t) => t.status === 'completed').length;
  const unclaimedCount = taskUnits.filter((u) => u.status === 'unclaimed').length;
  const failedCount = taskUnits.filter((u) => u.status === 'failed').length;
  const avgProgress = tasks.length > 0 ? Math.round(tasks.reduce((s, t) => s + t.progress, 0) / tasks.length) : 0;
  const selectedUnits = selectedTask ? taskUnits.filter((u) => u.taskId === selectedTask.id) : [];

  if (loading) return (<div className="p-6 space-y-4"><div className="h-6 w-32 bg-background-200 rounded animate-pulse" /><div className="grid grid-cols-1 lg:grid-cols-3 gap-4"><div className="lg:col-span-2 space-y-3">{[1, 2, 3, 4].map((i) => (<div key={i} className="h-20 bg-background-200 rounded-lg animate-pulse" />))}</div></div></div>);

  return (
    // 详情打开时宽屏(xl+)让出抽屉宽度 —— TaskDetailPanel 是 fixed right-0 w-[480px],
    // 不让的话它直接压在任务列表上。与 devices 页同一处理(那边还多一层遮罩要关掉,
    // 这里没有遮罩, 所以只需要留白)。fixed 的抽屉自身不受这个 padding 影响。
    <div className={`p-4 lg:p-6 transition-[padding] duration-200 ${selectedTask ? 'xl:pr-[496px]' : ''}`}>
      <div className="flex items-start justify-between gap-3 mb-2 animate-fade-in-up">
        <div><h1 className="text-lg font-bold text-foreground-900 font-heading">任务调度</h1><p className="text-xs text-foreground-500">任务台账 · 认领租约 · 带证据完成</p></div>
        <button onClick={() => setShowCreateModal(true)} className="flex items-center gap-1.5 px-3 py-2 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-md text-xs font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap btn-lift shrink-0"><i className="ri-add-line text-sm" />创建任务</button>
      </div>

      {error && <div className="my-3"><LoadError error={error} onRetry={refetch} /></div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-3 mb-4 lg:mb-5">
        <StatCard label="活跃任务" value={activeCount} color="bg-accent-500/10 text-accent-600" icon="ri-task-line" />
        <StatCard label="已完成" value={completedCount} color="bg-green-500/10 text-green-600" icon="ri-check-double-line" />
        <StatCard label="待认领单元" value={unclaimedCount} color="bg-amber-500/10 text-amber-600" icon="ri-hand-coin-line" />
        <StatCard label="失败单元" value={failedCount} color="bg-red-500/10 text-red-500" icon="ri-error-warning-line" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 lg:gap-4 animate-fade-in-up" style={{ animationDelay: '0.14s' }}>
        <div className="lg:col-span-2"><TaskList tasks={tasks} taskUnits={taskUnits} onSelectTask={setSelectedTask} onCreateTask={() => setShowCreateModal(true)} /></div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-3 lg:p-4">
          <h3 className="text-xs font-bold text-foreground-800 mb-3">任务效率</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center"><span className="text-[11px] text-foreground-500">平均完成率</span><span className="text-sm font-mono font-bold text-foreground-900">{avgProgress}%</span></div>
            <div className="h-1.5 bg-background-200 rounded-full overflow-hidden"><div className="h-full rounded-full bg-primary-500 transition-all" style={{ width: `${avgProgress}%` }} /></div>
            <p className="text-[10px] text-foreground-400 leading-relaxed">按工作单元数出来的,不是任务自报的进度。租约到期的单元会被回收回"待认领",所以进度可能回退 —— 那是对的。</p>
          </div>
        </div>
      </div>

      <CreateTaskModal open={showCreateModal} onClose={() => setShowCreateModal(false)} onCreated={refetch} />
      {selectedTask && <TaskDetailPanel task={selectedTask} units={selectedUnits} onClose={() => setSelectedTask(null)} />}
    </div>
  );
}
